import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fork, put, takeEvery } from "redux-saga/effects";
import { AxiosResponse } from "axios";

import { backend } from "../../utils/http";
import { UserInList } from "../../user-management/store";

export interface CampaignsAccessData {
  [campaignUUID: string]: CampaignAccessData;
}

export type CampaignAccessData = string[];

interface CampaignAccessDataRequestInterface {
  campaignUUID: string;
  clientUUID: string;
}

export interface CampaignAccessManagementState {
  getCampaignsAccessData: {
    loading: boolean;
    success: boolean | null;
    error: string;
  };
  getCampaignAccessData: {
    loading: boolean;
    success: boolean | null;
    error: string;
  };
  updateUsersAccess: {
    loading: boolean;
    success: boolean | null;
    error: string;
  };
  campaignAccessData: CampaignsAccessData;
}

export interface CampaignAccessDataUser extends UserInList {
  hasAccessToCampaign: boolean;
}

export enum CampaignAccessChangeOrigin {
  DRAFT = "draft",
  CAMPAIGN_HOME = "campaign home",
  CAMPAIGN_ADMIN_MANAGEMENT = "campaign admin management",
}

export interface UpdateUsersAccessRequestData {
  clientUUID: string;
  campaignUUID: string;
  enabledUsers: string[];
  disabledUsers: string[];
  accessOrigin: CampaignAccessChangeOrigin;
}

interface UpdateUsersAccessResponseData {
  campaignUUID: string;
  users: string[];
}

interface CampaignAccessResponseData {
  campaignUUID: string;
  users: string[];
}

const initialState = {
  getCampaignsAccessData: {
    loading: false,
    success: null,
    error: "",
  },
  getCampaignAccessData: {
    loading: false,
    success: null,
    error: "",
  },
  updateUsersAccess: {
    loading: false,
    success: null,
    error: "",
  },
  campaignAccessData: {},
} as CampaignAccessManagementState;

export const campaignAccessManagementSlice = createSlice({
  name: "campaign access management",
  initialState: initialState,
  reducers: {
    setInitialUpdateUsersAccess(state) {
      state.updateUsersAccess = initialState.updateUsersAccess;
    },
    setInitialState(state) {
      state.getCampaignsAccessData = initialState.getCampaignsAccessData;
      state.getCampaignAccessData = initialState.getCampaignAccessData;
      state.updateUsersAccess = initialState.updateUsersAccess;
      state.campaignAccessData = {};
    },
    getCampaignsAccessData(state, payload: PayloadAction<string>) {
      state.getCampaignsAccessData.loading = true;
      state.getCampaignsAccessData.error = "";
      state.getCampaignsAccessData.success = null;
      state.campaignAccessData = {};
    },
    getCampaignsAccessDataSuccess(state, action) {
      state.getCampaignsAccessData.loading = false;
      state.getCampaignsAccessData.error = "";
      state.getCampaignsAccessData.success = true;
      state.campaignAccessData = action.payload.data;
    },
    getCampaignsAccessDataFailure(state, action) {
      state.getCampaignsAccessData.loading = false;
      state.getCampaignsAccessData.error =
        action.payload.data?.message || "There was an error. Please try again";
      state.getCampaignsAccessData.success = null;
      state.campaignAccessData = {};
    },
    getCampaignAccessData(
      state,
      payload: PayloadAction<CampaignAccessDataRequestInterface>
    ) {
      state.getCampaignAccessData.loading = true;
      state.getCampaignAccessData.error = "";
      state.getCampaignAccessData.success = null;
      state.campaignAccessData = {};
    },
    getCampaignAccessDataSuccess(state, action) {
      state.getCampaignAccessData.loading = false;
      state.getCampaignAccessData.error = "";
      state.getCampaignAccessData.success = true;
      const responseData: CampaignAccessResponseData = action.payload.data;
      state.campaignAccessData[responseData.campaignUUID] = [
        ...responseData.users,
      ];
    },
    getCampaignAccessDataFailure(state, action) {
      state.getCampaignAccessData.loading = false;
      state.getCampaignAccessData.error =
        action.payload.data?.message || "There was an error. Please try again";
      state.getCampaignAccessData.success = null;
      state.campaignAccessData = {};
    },
    updateUsersAccess(
      state,
      payload: PayloadAction<UpdateUsersAccessRequestData>
    ) {
      state.updateUsersAccess.loading = true;
      state.updateUsersAccess.error = "";
      state.updateUsersAccess.success = null;
    },
    updateUsersAccessSuccess(state, action) {
      state.updateUsersAccess.loading = false;
      state.updateUsersAccess.error = "";
      state.updateUsersAccess.success = true;

      const responseData: UpdateUsersAccessResponseData = action.payload.data;
      state.campaignAccessData[responseData.campaignUUID] = [
        ...responseData.users,
      ];
    },
    updateUsersAccessFailure(state, action) {
      state.updateUsersAccess.loading = false;
      state.updateUsersAccess.error =
        action.payload.data?.message || "There was an error. Please try again";
      state.updateUsersAccess.success = null;
    },
  },
});

function* getCampaignsAccessDataSaga() {
  yield takeEvery(
    campaignAccessManagementSlice.actions.getCampaignsAccessData,
    function* (action) {
      try {
        const response: AxiosResponse<CampaignsAccessData[]> =
          yield backend.campaignBackend.getCampaignsAccessData(action.payload);

        yield put(
          campaignAccessManagementSlice.actions.getCampaignsAccessDataSuccess(
            response
          )
        );
      } catch (err) {
        yield put(
          campaignAccessManagementSlice.actions.getCampaignsAccessDataFailure(
            err
          )
        );
      }
    }
  );
}

function* getCampaignAccessDataSaga() {
  yield takeEvery(
    campaignAccessManagementSlice.actions.getCampaignAccessData,
    function* (action) {
      try {
        const response: AxiosResponse<CampaignAccessData> =
          yield backend.campaignBackend.getCampaignAccessData(
            action.payload.clientUUID,
            action.payload.campaignUUID
          );

        yield put(
          campaignAccessManagementSlice.actions.getCampaignAccessDataSuccess(
            response
          )
        );
      } catch (err) {
        yield put(
          campaignAccessManagementSlice.actions.getCampaignAccessDataFailure(
            err
          )
        );
      }
    }
  );
}

function* updateUsersAccessSaga() {
  yield takeEvery(
    campaignAccessManagementSlice.actions.updateUsersAccess,
    function* (action) {
      try {
        const response: AxiosResponse<UpdateUsersAccessResponseData> =
          yield backend.campaignBackend.updateUsersAccess(action.payload);

        yield put(
          campaignAccessManagementSlice.actions.updateUsersAccessSuccess(
            response
          )
        );
      } catch (err) {
        yield put(
          campaignAccessManagementSlice.actions.updateUsersAccessFailure(err)
        );
      }
    }
  );
}

export function* campaignAccessManagementSaga() {
  yield fork(getCampaignsAccessDataSaga);
  yield fork(getCampaignAccessDataSaga);
  yield fork(updateUsersAccessSaga);
}

export const {
  setInitialState,
  setInitialUpdateUsersAccess,
  getCampaignsAccessData,
  getCampaignAccessData,
  updateUsersAccess,
} = campaignAccessManagementSlice.actions;
